import { useCallback } from 'react';
import {
  ActionOutboxEntityType,
  CatalogResults,
  EventWithData,
  Listing,
  PurchaseOrderTicketGroup,
  Sale,
} from 'src/WebApiController';

import {
  useCatalogDataContextDataSelector,
  useCatalogDataContextEventsSelector,
  useCatalogDataContextIsItemsLoadingSelector,
  useCatalogDataContextUpdateItemInEventSelector,
} from '../CatalogDataContext';
import { useSearchDataContext } from './SearchDataContext';

type SearchDataContextWithFallback = {
  data?: CatalogResults;
  isItemsLoading: boolean;
  eventsTransformed?: EventWithData[];
  totalItemsCount?: number | null;
  sortedItemIds?: number[] | null;
  isRefreshingAfterBulkEdit: boolean;
  updateItemInEvent: (
    item: Listing | Sale | PurchaseOrderTicketGroup,
    entityType: ActionOutboxEntityType,
    sectionalUpdate?: boolean
  ) => void;
  refetch: () => void;
};

/**
 * Use this hook to get the search data context with a fallback to the catalog data context.
 * This is useful when we want to get the search data source when intended (for example, search page)
 * and fallback to the catalog data context when not intended (outside of search page).
 */
export const useSearchDataContextWithCatalogFallback =
  (): SearchDataContextWithFallback => {
    const searchDataContext = useSearchDataContext();
    const data = useCatalogDataContextDataSelector();
    const eventsTransformed = useCatalogDataContextEventsSelector();
    const isItemsLoading = useCatalogDataContextIsItemsLoadingSelector();
    const updateItemInEvent = useCatalogDataContextUpdateItemInEventSelector();

    const dummyRefetch = useCallback(() => {}, []);

    if (searchDataContext.isEnabled) {
      return {
        data: searchDataContext.data,
        isItemsLoading: searchDataContext.isLoading,
        eventsTransformed: searchDataContext.eventsTransformed,
        totalItemsCount: searchDataContext.totalItemsCount,
        sortedItemIds: searchDataContext.sortedItemIds,
        isRefreshingAfterBulkEdit: searchDataContext.isRefreshingAfterBulkEdit,
        updateItemInEvent: searchDataContext.updateItemInEvent,
        refetch: searchDataContext.refetch,
      };
    }
    return {
      data,
      isItemsLoading,
      eventsTransformed,
      totalItemsCount: null,
      sortedItemIds: null,
      isRefreshingAfterBulkEdit: false,
      updateItemInEvent,
      refetch: dummyRefetch,
    };
  };
